// three.js preview of the coaster: one mesh per part, orbit by drag. Adapted from apps/coffee-cup/src/preview3d.js.
// Core meshes are z-up; group.rotation.set(-PI/2, 0, 0) maps core (x, y, z) to three (x, z, -y). The camera is
// (R sin(phi) cos(theta), R cos(phi), R sin(phi) sin(theta)), so theta = PI/2 puts the eye in the south (core -y):
// seat A, north up and east right, 35 degrees above the table.
// setScene([{ name, mesh, color }]). The camera is fitted to the coaster's OUTLINE (a few points taken from the scene
// itself, see outlineOf), not to its axis-aligned box, and refitted at the current angle on every camera change: a disc
// keeps one size as the camera turns round it. setView('a' | 'b' | 'top' | 'front') puts the camera on a seat (or above, or in the south for a logo) and refits, so a
// view is framed the same whenever it is chosen. swing() turns the camera A -> B -> A once, through the east.
import * as THREE from 'three';
const PHI_EDGE = 0.05, THETA0 = Math.PI / 2, PHI0 = Math.PI / 2 - (35 * Math.PI) / 180, FILL = 0.94;
const SWING_MS = 3200, BINS = 72;
// [theta, phi]: seats A and B at 35 degrees of elevation; "top" is 87 degrees, the drag's own limit, keeping seat A's orientation;
// "front" is the eye in the south, 55 degrees above the table (phi counts from the vertical), the logo style's view.
const VIEWS = { a: [THETA0, PHI0], b: [-THETA0, PHI0], top: [THETA0, PHI_EDGE], front: [THETA0, (35 * Math.PI) / 180] };
export function createPreview(canvas, { onView } = {}) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(35, 4 / 3, 1, 1000);
  let camR = 90, camTheta = THETA0, camPhi = PHI0;
  const placeCamera = () => {
    camera.position.set(camR * Math.sin(camPhi) * Math.cos(camTheta), camR * Math.cos(camPhi), camR * Math.sin(camPhi) * Math.sin(camTheta));
    camera.lookAt(0, 0, 0);
  };
  // The key light rides on the camera, a few degrees off the view axis, so from above a flat face mirrors it straight into
  // the eye. A rough material (0.85, was 0.45) keeps that highlight from washing the Black body grey in the view from above.
  const key = new THREE.DirectionalLight(0xffffff, 1.4); key.position.set(-30, 40, 0); camera.add(key);
  const rim = new THREE.DirectionalLight(0xffffff, 0.9); rim.position.set(30, 10, -40); camera.add(rim);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x888888, 1.2), camera);
  const group = new THREE.Group(); group.rotation.set(-Math.PI / 2, 0, 0); scene.add(group);
  const meshes = new Map();
  const render = () => renderer.render(scene, camera);
  // Outline points (group-local, core coordinates): per part, per angular bin around the vertical axis through the union's
  // centre, the farthest vertex of the lower half and of the upper half of the part's height. A disc gives a ring of points,
  // a square its corners and edges. Computed once per scene.
  let outline = [];
  function outlineOf(geoms, c) {
    const pts = [];
    for (const geo of geoms) {
      const a = geo.getAttribute('position'), bx = geo.boundingBox, mid = (bx.min.z + bx.max.z) / 2;
      const best = [new Array(BINS).fill(null), new Array(BINS).fill(null)];
      for (let i = 0; i < a.count; i++) {
        const x = a.getX(i), y = a.getY(i), z = a.getZ(i), dx = x - c.x, dy = y - c.y;
        const bin = Math.min(BINS - 1, Math.floor(((Math.atan2(dy, dx) + Math.PI) / (2 * Math.PI)) * BINS)), half = z > mid ? 1 : 0;
        const score = Math.hypot(dx, dy) + (half ? z : -z) * 1e-4;      // ties go to the outermost height of the half
        if (!best[half][bin] || score > best[half][bin].score) best[half][bin] = { score, x, y, z };
      }
      for (const h of best) for (const b of h) if (b) pts.push(new THREE.Vector3(b.x, b.y, b.z));
    }
    return pts;
  }
  const worldPoints = () => { group.updateMatrixWorld(true); return outline.map((p) => p.clone().applyMatrix4(group.matrixWorld)); };
  function setScene(parts) {
    const geos = new Set();
    for (const c of [...group.children]) { group.remove(c); geos.add(c.geometry); c.material.dispose(); }
    for (const g of geos) g.dispose();
    meshes.clear();
    const bb = new THREE.Box3();
    const geoms = [];
    for (const p of parts) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(p.mesh.vertProperties), p.mesh.numProp));
      geo.setIndex(new THREE.BufferAttribute(new Uint32Array(p.mesh.triVerts), 1));
      geo.computeVertexNormals(); geo.computeBoundingBox();
      const obj = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: p.color, roughness: 0.85, metalness: 0.05, flatShading: true }));
      bb.union(geo.boundingBox); geoms.push(geo);
      group.add(obj);
      if (p.name) meshes.set(p.name, obj);
    }
    const c = bb.getCenter(new THREE.Vector3());
    outline = outlineOf(geoms, c);
    group.position.set(-c.x, -c.z, c.y);        // core centre of the union → the origin the camera looks at
    group.updateMatrixWorld(true);
    fit(); render();
  }
  // Back the camera off until the projected outline fills FILL of the canvas (width and height) at the CURRENT angle, then
  // centre its vertical extent on the canvas. A function of (scene, canvas, angle) only: call it after every angle change.
  let swinging = false;
  function fit() {
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    camera.aspect = r.width / r.height;
    const pts = worldPoints();
    if (!pts.length) return;
    const size = new THREE.Box3().setFromPoints(pts).getSize(new THREE.Vector3());
    camR = Math.max(size.x, size.y, size.z, 10) * 3;
    const extents = () => {                          // NDC extents without a view offset
      camera.clearViewOffset();
      camera.far = Math.max(1000, camR * 4); camera.updateProjectionMatrix();
      placeCamera(); camera.updateMatrixWorld(true);
      let mx = 0, lo = Infinity, hi = -Infinity;
      for (const p of pts) { const v = p.clone().project(camera); mx = Math.max(mx, Math.abs(v.x)); lo = Math.min(lo, v.y); hi = Math.max(hi, v.y); }
      return { mx, lo, hi };
    };
    for (let i = 0; i < 5; i++) { const e = extents(); camR *= Math.max(e.mx, (e.hi - e.lo) / 2) / FILL; }
    const e = extents();
    camera.setViewOffset(r.width, r.height, 0, (-((e.lo + e.hi) / 2) * r.height) / 2, r.width, r.height);
  }
  function project(pts) {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pts) {
      const v = p.clone().project(camera);
      const x = (v.x * 0.5 + 0.5) * w, y = (1 - (v.y * 0.5 + 0.5)) * h;
      minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
    return { left: minX, top: minY, right: maxX, bottom: maxY };
  }
  // The scene's outline projected, as its bounding rectangle in canvas pixels.
  function sceneRect() {
    const pts = worldPoints();
    if (!pts.length) return null;
    camera.updateMatrixWorld(true);
    return project(pts);
  }
  // A part's bounding box projected to canvas pixels (relative to the canvas), null when it is not in the scene.
  function rectOf(name) {
    const m = meshes.get(name);
    if (!m) return null;
    group.updateMatrixWorld(true); camera.updateMatrixWorld(true);
    const b = new THREE.Box3().setFromObject(m), pts = [];
    for (const x of [b.min.x, b.max.x]) for (const y of [b.min.y, b.max.y]) for (const z of [b.min.z, b.max.z]) pts.push(new THREE.Vector3(x, y, z));
    return project(pts);
  }
  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false); fit(); render();
  }
  const names = Object.keys(VIEWS);
  function view() {
    if (swinging) return null;              // a swing is no named view: no pill is lit while it runs
    for (const n of names) if (Math.abs(camTheta - VIEWS[n][0]) < 1e-3 && Math.abs(camPhi - VIEWS[n][1]) < 1e-3) return n;
    return null;
  }
  const changed = () => { if (onView) onView(view()); };
  let raf = 0;
  let touched = false;                      // the visitor has chosen a view or pressed the canvas: no first swing after that
  function stopSwing() { if (!swinging) return; swinging = false; cancelAnimationFrame(raf); }
  function setView(name) {
    if (!Object.hasOwn(VIEWS, name)) return;
    stopSwing(); touched = true;
    [camTheta, camPhi] = VIEWS[name];
    fit(); render(); changed();
  }
  // A -> B -> A once: theta runs from PI/2 down to -PI/2 (through the east, x > 0) and back, eased at both ends.
  function swing() {
    if (swinging || touched) return;
    [camTheta, camPhi] = VIEWS.a;
    swinging = true;
    fit(); render(); changed();
    let t0 = null;
    const tick = (now) => {
      if (!swinging) return;
      if (t0 === null) t0 = now;
      const u = Math.min(1, (now - t0) / SWING_MS);
      if (u >= 1) { setView('a'); return; }
      camTheta = THETA0 - Math.PI * ((1 - Math.cos(2 * Math.PI * u)) / 2);
      fit(); render(); changed();          // framed at this angle, every frame
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  }
  let dragging = false, lx = 0, ly = 0;
  const touch = () => { touched = true; stopSwing(); };
  canvas.addEventListener('wheel', touch, { passive: true });
  canvas.addEventListener('pointerdown', (e) => { touch(); dragging = true; lx = e.clientX; ly = e.clientY; canvas.setPointerCapture(e.pointerId); });
  canvas.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    camTheta -= (e.clientX - lx) * 0.008; camPhi = Math.min(Math.PI - PHI_EDGE, Math.max(PHI_EDGE, camPhi - (e.clientY - ly) * 0.006));
    lx = e.clientX; ly = e.clientY; fit(); render(); changed();
  });
  canvas.addEventListener('pointerup', () => { dragging = false; });
  new ResizeObserver(resize).observe(canvas); resize();
  return { setScene, setView, view, angles: () => ({ theta: camTheta, phi: camPhi }), swing, swinging: () => swinging, refit: () => { fit(); render(); }, sceneRect, rectOf, count: () => group.children.length };
}
